import React from 'react';
import { motion } from 'framer-motion';
import { wrappedTranslations } from '../../../translations/wrapped';

interface IntroCardProps {
    name: string;
    club: string;
    classification: string;
    swissRank?: string;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const IntroCard: React.FC<IntroCardProps> = ({ name, club, classification, swissRank, lang }) => {
    const t = wrappedTranslations[lang];

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.img
                    src="/app-icon.png"
                    alt="TTStats"
                    className="h-24 w-24 md:h-32 md:w-32 mb-8 mx-auto drop-shadow-[0_0_25px_rgba(28,122,214,0.4)]"
                    initial={{ opacity: 0, scale: 0, rotate: -180 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
                />

                <motion.h1
                    className="text-5xl sm:text-7xl md:text-9xl font-bold mb-4 tracking-tight"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                >
                    {t.wrappedTitle.split('').map((char, index) => (
                        <span
                            key={index}
                            className={index < 2 ? 'text-[#1C7AD6]' : 'text-gray-900'}
                        >
                            {char}
                        </span>
                    ))}
                </motion.h1>

                <motion.h2
                    className="text-sm md:text-base font-light mb-8 md:mb-12 text-gray-600 tracking-widest uppercase"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                >
                    {t.wrappedSubtitle}
                </motion.h2>

                <motion.div
                    className="relative w-full max-w-2xl"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 }}
                >
                    <div className="px-6 py-6 md:px-8 md:py-8 bg-white rounded-2xl border border-gray-200 shadow-lg">
                        <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-4 tracking-tight break-words">
                            {name}
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl text-[#1C7AD6] font-medium tracking-wide">
                            {club}
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    className="mt-8 md:mt-12 flex flex-wrap justify-center gap-3 md:gap-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2 }}
                >
                    <span className="px-3 py-1.5 bg-[#1C7AD6] text-white rounded-lg text-sm font-bold shadow-lg">
                        {classification}
                    </span>
                    {swissRank && (
                        <span className="px-3 py-1.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-bold shadow-md">
                            #{swissRank} in Switzerland
                        </span>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default IntroCard;
